import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, RefreshCw, Trash2, Inbox } from "lucide-react";
import Seo from "@/components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
  new: "bg-amber-100 text-amber-800",
  contacted: "bg-sky-100 text-sky-800",
  "in-progress": "bg-violet-100 text-violet-800",
  closed: "bg-emerald-100 text-emerald-800",
};

export default function AdminDashboard() {
  const [tab, setTab] = useState("enquiries");
  const [enquiries, setEnquiries] = useState([]);
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const token = localStorage.getItem("wisdom_admin_token");
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    if (!token) {
      navigate("/admin/login");
      return;
    }
    try {
      const [eq, cl] = await Promise.all([
        axios.get(`${API}/enquiries`, { headers }),
        axios.get(`${API}/claims`, { headers }),
      ]);
      setEnquiries(eq.data.enquiries);
      setClaims(cl.data.claims);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("wisdom_admin_token");
        navigate("/admin/login");
      } else {
        toast.error("Could not load data.");
      }
    } finally {
      setLoading(false);
    }
  }, [token, navigate]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (kind, id, status) => {
    try {
      await axios.patch(`${API}/${kind}/${id}`, { status }, { headers });
      const setter = kind === "enquiries" ? setEnquiries : setClaims;
      setter((prev) => prev.map((e) => (e.id === id ? { ...e, status } : e)));
      toast.success("Status updated.");
    } catch {
      toast.error("Could not update status.");
    }
  };

  const remove = async (kind, id) => {
    try {
      await axios.delete(`${API}/${kind}/${id}`, { headers });
      const setter = kind === "enquiries" ? setEnquiries : setClaims;
      setter((prev) => prev.filter((e) => e.id !== id));
      toast.success("Deleted.");
    } catch {
      toast.error("Could not delete.");
    }
  };

  const logout = () => {
    localStorage.removeItem("wisdom_admin_token");
    navigate("/admin/login");
  };

  const newEnquiries = enquiries.filter((e) => e.status === "new").length;
  const newClaims = claims.filter((c) => c.status === "new").length;

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-slate-100">
      <Seo title="Admin Dashboard | Wisdom Insurance Broking" description="Admin enquiries and claims dashboard." />
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-3 px-4 py-4 sm:px-8">
          <div className="flex items-center gap-3">
            <img src="/logo-dark.png" alt="Wisdom Insurance Broking" className="h-9 w-auto" />
            <div>
              <h1 className="font-heading text-base font-bold text-navy">Admin Dashboard</h1>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                {enquiries.length} enquiries · {claims.length} claims
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="admin-refresh-btn" onClick={load} className="flex items-center gap-1.5 border border-slate-300 px-3 py-2 text-xs font-semibold text-slate-600 hover:border-amber-600 hover:text-amber-700">
              <RefreshCw className="h-3.5 w-3.5" /> Refresh
            </button>
            <button data-testid="admin-logout-btn" onClick={logout} className="flex items-center gap-1.5 bg-navy px-3 py-2 text-xs font-semibold text-white hover:bg-amber-700">
              <LogOut className="h-3.5 w-3.5" /> Logout
            </button>
          </div>
        </div>
        <div className="mx-auto flex max-w-7xl gap-1 px-4 sm:px-8">
          <button
            data-testid="tab-enquiries"
            onClick={() => setTab("enquiries")}
            className={`border-b-2 px-4 py-2.5 text-sm font-semibold transition-colors ${tab === "enquiries" ? "border-amber-600 text-navy" : "border-transparent text-slate-500 hover:text-navy"}`}
          >
            Enquiries {newEnquiries > 0 && <span className="ml-1.5 bg-amber-600 px-1.5 py-0.5 text-[10px] font-bold text-white">{newEnquiries}</span>}
          </button>
          <button
            data-testid="tab-claims"
            onClick={() => setTab("claims")}
            className={`border-b-2 px-4 py-2.5 text-sm font-semibold transition-colors ${tab === "claims" ? "border-amber-600 text-navy" : "border-transparent text-slate-500 hover:text-navy"}`}
          >
            Claim Intimations {newClaims > 0 && <span className="ml-1.5 bg-red-700 px-1.5 py-0.5 text-[10px] font-bold text-white">{newClaims}</span>}
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-8">
        {loading ? (
          <p className="py-20 text-center text-sm text-slate-500" data-testid="admin-loading">Loading...</p>
        ) : tab === "enquiries" ? (
          enquiries.length === 0 ? (
            <div className="flex flex-col items-center gap-3 py-20 text-slate-400" data-testid="admin-empty-state">
              <Inbox className="h-10 w-10" strokeWidth={1.5} />
              <p className="text-sm">No enquiries yet. New website submissions will appear here.</p>
            </div>
          ) : (
            <div className="overflow-x-auto border border-slate-200 bg-white" data-testid="enquiries-table-wrapper">
              <table className="w-full min-w-[900px] text-left text-sm">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50 font-mono text-[11px] uppercase tracking-wider text-slate-500">
                    <th className="px-4 py-3">Received</th>
                    <th className="px-4 py-3">Name / Company</th>
                    <th className="px-4 py-3">Contact</th>
                    <th className="px-4 py-3">Insurance Type</th>
                    <th className="px-4 py-3">Message</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {enquiries.map((e) => (
                    <tr key={e.id} className="border-b border-slate-100 align-top last:border-b-0 hover:bg-slate-50" data-testid={`enquiry-row-${e.id.slice(0, 8)}`}>
                      <td className="whitespace-nowrap px-4 py-3 text-xs text-slate-500">
                        {new Date(e.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                      </td>
                      <td className="px-4 py-3">
                        <p className="font-semibold text-navy">{e.name}</p>
                        <p className="text-xs text-slate-500">{e.company || "—"} {e.city ? `· ${e.city}` : ""}</p>
                      </td>
                      <td className="px-4 py-3 text-xs">
                        <p>{e.phone}</p>
                        <p className="text-slate-500">{e.email}</p>
                      </td>
                      <td className="px-4 py-3 text-xs font-medium text-slate-700">{e.insurance_type}</td>
                      <td className="max-w-[240px] px-4 py-3 text-xs text-slate-600">{e.message || "—"}</td>
                      <td className="px-4 py-3">
                        <select
                          data-testid={`status-select-${e.id.slice(0, 8)}`}
                          value={e.status}
                          onChange={(ev) => setStatus("enquiries", e.id, ev.target.value)}
                          className={`border-0 px-2 py-1 text-xs font-semibold ${STATUS_STYLES[e.status] || ""}`}
                        >
                          <option value="new">New</option>
                          <option value="contacted">Contacted</option>
                          <option value="closed">Closed</option>
                        </select>
                      </td>
                      <td className="px-4 py-3">
                        <button data-testid={`delete-btn-${e.id.slice(0, 8)}`} onClick={() => remove("enquiries", e.id)} className="p-1.5 text-slate-400 hover:text-red-600" aria-label="Delete enquiry">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        ) : claims.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-20 text-slate-400" data-testid="admin-empty-claims">
            <Inbox className="h-10 w-10" strokeWidth={1.5} />
            <p className="text-sm">No claim intimations yet.</p>
          </div>
        ) : (
          <div className="overflow-x-auto border border-slate-200 bg-white" data-testid="claims-table-wrapper">
            <table className="w-full min-w-[900px] text-left text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 font-mono text-[11px] uppercase tracking-wider text-slate-500">
                  <th className="px-4 py-3">Received</th>
                  <th className="px-4 py-3">Name / Company</th>
                  <th className="px-4 py-3">Contact</th>
                  <th className="px-4 py-3">Category</th>
                  <th className="px-4 py-3">Loss</th>
                  <th className="px-4 py-3">What happened</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {claims.map((c) => (
                  <tr key={c.id} className="border-b border-slate-100 align-top last:border-b-0 hover:bg-slate-50" data-testid={`claim-row-${c.id.slice(0, 8)}`}>
                    <td className="whitespace-nowrap px-4 py-3 text-xs text-slate-500">
                      {new Date(c.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-semibold text-navy">{c.name}</p>
                      <p className="text-xs text-slate-500">{c.company || "—"}</p>
                    </td>
                    <td className="px-4 py-3 text-xs">
                      <p>{c.phone}</p>
                      <p className="text-slate-500">{c.email}</p>
                    </td>
                    <td className="px-4 py-3 text-xs font-medium text-slate-700">{c.policy_category}</td>
                    <td className="px-4 py-3 text-xs text-slate-600">
                      <p>{c.date_of_loss || "—"}</p>
                      <p className="text-slate-500">{c.loss_location || ""}</p>
                    </td>
                    <td className="max-w-[240px] px-4 py-3 text-xs text-slate-600">{c.description}</td>
                    <td className="px-4 py-3">
                      <select
                        data-testid={`claim-status-select-${c.id.slice(0, 8)}`}
                        value={c.status}
                        onChange={(ev) => setStatus("claims", c.id, ev.target.value)}
                        className={`border-0 px-2 py-1 text-xs font-semibold ${STATUS_STYLES[c.status] || ""}`}
                      >
                        <option value="new">New</option>
                        <option value="in-progress">In Progress</option>
                        <option value="closed">Closed</option>
                      </select>
                    </td>
                    <td className="px-4 py-3">
                      <button data-testid={`claim-delete-btn-${c.id.slice(0, 8)}`} onClick={() => remove("claims", c.id)} className="p-1.5 text-slate-400 hover:text-red-600" aria-label="Delete claim">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}
